import { z } from 'zod';
import { PURCHASE_ORDER_STATUSES } from '../constants/purchase-order-states.js';
import { VENDOR_PAYMENT_STATUSES } from '../constants/purchasing.js';
import { moneyCentsSchema, uuidSchema } from './common.js';
import { intakeUnitSchema } from './inventory.js';
export const purchaseOrderItemInputSchema = z.object({
    productId: uuidSchema,
    qty: z.number().int().positive().max(9999),
    unitCostCents: moneyCentsSchema.nullable().default(null),
    note: z.string().trim().max(500).nullable().default(null),
});
/** t1223: an office-supplies line on a split invoice — goes to expenses, not stock. */
export const purchaseOrderExpenseLineSchema = z.object({
    description: z.string().trim().min(1).max(200),
    qty: z.number().int().positive().max(9999),
    unitCostCents: moneyCentsSchema,
});
export const createPurchaseOrderSchema = z
    .object({
    vendor: z.string().trim().min(1).max(120),
    invoiceRef: z.string().trim().max(64).nullable().default(null),
    expectedLocationId: uuidSchema.nullable().default(null),
    note: z.string().trim().max(1000).nullable().default(null),
    /** Order-level amounts from the invoice; not stock lines. */
    shippingCents: moneyCentsSchema.nullable().default(null),
    taxCents: moneyCentsSchema.nullable().default(null),
    /** t1169: the cost entries made on receiving start with this status. */
    paymentStatus: z.enum(VENDOR_PAYMENT_STATUSES).default('unpaid'),
    /** t1169: spread shipping / tax over the received items' cost. */
    allocateShipping: z.boolean().default(false),
    allocateTax: z.boolean().default(false),
    items: z.array(purchaseOrderItemInputSchema).min(1).max(200),
    /** t1223: office supplies on the same invoice; saved as an unpaid expense tied to this order. */
    expenseLines: z.array(purchaseOrderExpenseLineSchema).max(200).default([]),
    expenseCategoryId: uuidSchema.nullable().default(null),
})
    .refine((v) => v.expenseLines.length === 0 || v.expenseCategoryId, {
    message: 'Choose a category for the office supplies',
    path: ['expenseCategoryId'],
});
/** `items` replaces the whole line list and is refused once anything is received. */
export const updatePurchaseOrderSchema = z.object({
    vendor: z.string().trim().min(1).max(120).optional(),
    invoiceRef: z.string().trim().max(64).nullable().optional(),
    expectedLocationId: uuidSchema.nullable().optional(),
    note: z.string().trim().max(1000).nullable().optional(),
    shippingCents: moneyCentsSchema.nullable().optional(),
    taxCents: moneyCentsSchema.nullable().optional(),
    paymentStatus: z.enum(VENDOR_PAYMENT_STATUSES).optional(),
    allocateShipping: z.boolean().optional(),
    allocateTax: z.boolean().optional(),
    items: z.array(purchaseOrderItemInputSchema).min(1).max(200).optional(),
});
export const purchaseOrderNoteSchema = z.object({
    note: z.string().trim().max(500).nullable().default(null),
});
/**
 * One line per order item. A counted line carries `qty`; a device/SIM line
 * carries the scanned `units`. Receiving less than is pending is fine — the
 * rest stays pending.
 */
export const receivePurchaseOrderLineSchema = z.union([
    z.object({ itemId: uuidSchema, units: z.array(intakeUnitSchema).min(1).max(500) }),
    z.object({ itemId: uuidSchema, qty: z.number().int().positive().max(9999) }),
]);
export const receivePurchaseOrderSchema = z.object({
    locationId: uuidSchema,
    note: z.string().trim().max(500).nullable().default(null),
    /** t1169: the order is already paid — mark it paid before the cost entries are made. */
    markPaid: z.boolean().default(false),
    lines: z.array(receivePurchaseOrderLineSchema).min(1).max(200),
});
export const receivePurchaseOrderResultSchema = z.object({
    receivedUnits: z.number().int(),
    receivedQty: z.number().int(),
    rejected: z.array(z.object({ itemId: uuidSchema, imei: z.string(), reason: z.string() })),
    status: z.enum(PURCHASE_ORDER_STATUSES),
});
export const purchaseOrderItemSchema = z.object({
    id: uuidSchema,
    productId: uuidSchema,
    productName: z.string(),
    sku: z.string(),
    productType: z.string(),
    qty: z.number().int(),
    receivedQty: z.number().int(),
    pendingQty: z.number().int(),
    unitCostCents: z.number().int().nullable(),
    /** Unit cost plus this line's share of the shipping/tax the order adds to cost; null when none is added. */
    landedUnitCostCents: z.number().int().nullable(),
    note: z.string().nullable(),
});
export const purchaseOrderDocumentMetaSchema = z.object({
    filename: z.string(),
    contentType: z.string(),
    sizeBytes: z.number().int(),
    createdAt: z.iso.datetime(),
});
export const purchaseOrderSchema = z.object({
    id: uuidSchema,
    poNumber: z.string(),
    status: z.enum(PURCHASE_ORDER_STATUSES),
    vendor: z.string(),
    invoiceRef: z.string().nullable(),
    expectedLocationId: uuidSchema.nullable(),
    expectedLocationName: z.string().nullable(),
    note: z.string().nullable(),
    shippingCents: z.number().int().nullable(),
    taxCents: z.number().int().nullable(),
    paymentStatus: z.enum(VENDOR_PAYMENT_STATUSES),
    allocateShipping: z.boolean(),
    allocateTax: z.boolean(),
    createdByLabel: z.string().nullable(),
    document: purchaseOrderDocumentMetaSchema.nullable(),
    items: z.array(purchaseOrderItemSchema),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
export const purchaseOrderListItemSchema = purchaseOrderSchema
    .omit({ items: true, createdByLabel: true, document: true })
    .extend({
    hasDocument: z.boolean(),
    itemCount: z.number().int(),
    orderedQty: z.number().int(),
    pendingQty: z.number().int(),
});
export const purchaseOrderListQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(200).default(50),
    /** `open` = ordered + partially received. */
    status: z.union([z.enum(PURCHASE_ORDER_STATUSES), z.literal('open')]).optional(),
    q: z.string().trim().max(100).optional(),
    productId: uuidSchema.optional(),
});
export const purchaseOrderEventSchema = z.object({
    id: uuidSchema,
    fromStatus: z.enum(PURCHASE_ORDER_STATUSES).nullable(),
    toStatus: z.enum(PURCHASE_ORDER_STATUSES),
    actorType: z.enum(['staff', 'customer', 'kiosk', 'system']),
    actorLabel: z.string().nullable(),
    note: z.string().nullable(),
    createdAt: z.iso.datetime(),
});
/**
 * What the invoice reader pulled off an uploaded supplier invoice. A draft
 * only — nothing is saved; the admin reviews it in the New purchase order
 * dialog. `match` is the catalogue product the line was recognised as.
 */
export const invoiceExtractionSchema = z.object({
    vendor: z.string().nullable(),
    invoiceRef: z.string().nullable(),
    invoiceTotalCents: z.number().int().nullable(),
    shippingCents: z.number().int().nullable(),
    taxCents: z.number().int().nullable(),
    lines: z.array(z.object({
        description: z.string(),
        sku: z.string().nullable(),
        qty: z.number().int().positive(),
        unitCostCents: z.number().int().nullable(),
        match: z
            .object({ productId: uuidSchema, name: z.string(), sku: z.string(), type: z.string() })
            .nullable(),
    })),
});
//# sourceMappingURL=purchase-order.js.map